/**
 * Scelta multipla per i filtri della BI (Davide 03/11/2026: «devo poter
 * selezionare a scelta i fornitori / i canali che voglio»).
 *
 * Una pillola dice cosa e' scelto («Tutti i fornitori», «2 fornitori»); un tocco
 * apre un riquadro con le caselle. Nessuna scelta = tutti.
 */

import { useEffect, useId, useRef, useState } from "react";

export interface OpzioneScelta {
  id: string;
  nome: string;
}

const PILLOLA =
  "inline-flex h-[34px] min-w-0 items-center gap-1.5 rounded-full border border-line-2 bg-card px-3 text-[13px] font-semibold text-ink transition-[border-color] hover:border-ink-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 max-md:h-11 max-md:w-full max-md:justify-between max-md:text-[15px]";

export function SceltaMultipla({
  etichetta,
  tutti,
  singolare,
  plurale,
  opzioni,
  valore,
  onChange,
}: {
  /** Nome per chi usa il lettore di schermo, es. «Fornitori». */
  etichetta: string;
  /** Testo della pillola senza scelte, es. «Tutti i fornitori». */
  tutti: string;
  singolare: string;
  plurale: string;
  opzioni: OpzioneScelta[];
  valore: string[];
  onChange: (v: string[]) => void;
}) {
  const [aperto, setAperto] = useState(false);
  const radice = useRef<HTMLDivElement>(null);
  const bottone = useRef<HTMLButtonElement>(null);
  const idRiquadro = useId();

  // Si chiude con un tocco fuori o con Esc (e il fuoco torna sul bottone).
  useEffect(() => {
    if (!aperto) return;
    function fuori(e: MouseEvent | TouchEvent) {
      if (radice.current && !radice.current.contains(e.target as Node)) setAperto(false);
    }
    function tasto(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setAperto(false);
        bottone.current?.focus();
      }
    }
    document.addEventListener("mousedown", fuori);
    document.addEventListener("touchstart", fuori);
    document.addEventListener("keydown", tasto);
    return () => {
      document.removeEventListener("mousedown", fuori);
      document.removeEventListener("touchstart", fuori);
      document.removeEventListener("keydown", tasto);
    };
  }, [aperto]);

  function alterna(id: string) {
    onChange(valore.includes(id) ? valore.filter((v) => v !== id) : [...valore, id]);
  }

  const testo =
    valore.length === 0
      ? tutti
      : valore.length === 1
        ? (opzioni.find((o) => o.id === valore[0])?.nome ?? valore[0])
        : `${valore.length} ${plurale}`;

  return (
    <div ref={radice} className="relative min-w-0 max-md:w-full">
      <button
        ref={bottone}
        type="button"
        aria-label={etichetta}
        aria-haspopup="true"
        aria-expanded={aperto}
        aria-controls={aperto ? idRiquadro : undefined}
        onClick={() => setAperto((a) => !a)}
        className={PILLOLA}
      >
        <span className="truncate">{testo}</span>
        <span aria-hidden="true" className="text-[10px] text-ink-3">
          ▾
        </span>
      </button>
      {aperto && (
        <div
          id={idRiquadro}
          role="group"
          aria-label={etichetta}
          className="absolute left-0 top-full z-30 mt-1.5 max-h-[60vh] w-64 max-w-[calc(100vw-32px)] overflow-y-auto rounded-2xl border border-line-2 bg-card p-1.5 shadow-lg max-md:w-full"
        >
          <button
            type="button"
            onClick={() => onChange([])}
            aria-pressed={valore.length === 0}
            className="flex h-9 w-full items-center justify-between rounded-xl px-2.5 text-left text-[13px] font-semibold text-ink hover:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 max-md:h-11 max-md:text-[15px]"
          >
            <span>Tutti</span>
            {valore.length === 0 && <span aria-hidden="true">✓</span>}
          </button>
          {opzioni.length === 0 && (
            <p className="px-2.5 py-2 text-[12.5px] text-ink-3">Nessun {singolare} ancora.</p>
          )}
          {opzioni.map((o) => (
            <label
              key={o.id}
              className="flex h-9 cursor-pointer items-center gap-2.5 rounded-xl px-2.5 text-[13px] font-semibold text-ink hover:bg-paper-2 focus-within:ring-2 focus-within:ring-accent/40 max-md:h-11 max-md:text-[15px]"
            >
              <input
                type="checkbox"
                checked={valore.includes(o.id)}
                onChange={() => alterna(o.id)}
                className="size-4 shrink-0 accent-[var(--color-accent,currentColor)]"
              />
              <span className="min-w-0 truncate">{o.nome}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
